import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Org = {
  group_name: string
  group_description: string
  group_logo_url: string
  logo_url: string                  // API path to uploaded logo; '' when none
  updated_at?: string
  group_country: string
  group_headquarters_location: string
  organization_type: 'SINGLE_COMPANY' | 'HOLDING_SUBSIDIARIES'
  // Legal entity fields (used in Single Company mode; in Holding mode they
  // represent the group/holdco itself, not the subsidiaries).
  legal_name: string
  legal_form: string
  registration_number: string
  tax_id: string
  vat_number: string
  legal_address_line1: string
  legal_city: string
  legal_postal_code: string
  accounting_standard: string
  consolidation_enabled: boolean
  consolidation_method: '' | 'FULL' | 'EQUITY' | 'PROPORTIONAL'
  intercompany_elimination: boolean
  reporting_currency: string
  primary_contact_name: string
  primary_contact_email: string
  primary_contact_phone: string
}

type Subsidiary = {
  id: number
  code: string
  name: string
  legal_form: string
  country_code: string
  ownership_percent: string | null
  start_date: string | null
  end_date: string | null
  is_active: boolean
  currency_code: string
}

// ─────────────────── Constants ───────────────────
const TYPE_LABEL: Record<Org['organization_type'], string> = {
  SINGLE_COMPANY: 'Single Company',
  HOLDING_SUBSIDIARIES: 'Holding with Subsidiaries',
}

const TYPE_COLOR: Record<Org['organization_type'], string> = {
  SINGLE_COMPANY: 'bg-blue-100 text-blue-800',
  HOLDING_SUBSIDIARIES: 'bg-purple-100 text-purple-800',
}

const CONSOLIDATION_LABEL: Record<NonNullable<Org['consolidation_method']>, string> = {
  '': '—',
  FULL: 'Full consolidation',
  EQUITY: 'Equity method',
  PROPORTIONAL: 'Proportional',
}

const ON_OFF_COLOR: Record<'ON' | 'OFF', string> = {
  ON: 'bg-green-100 text-green-800',
  OFF: 'bg-gray-100 text-gray-600',
}

const ACCOUNTING_STANDARDS = [
  { value: 'IAS',     label: 'IAS / IFRS',  description: 'International Accounting Standards / IFRS' },
  { value: 'RAS',     label: 'RAS',         description: 'Romanian Accounting Standards (OMFP 1802/2014)' },
  { value: 'HGB',     label: 'HGB',         description: 'German Commercial Code (Handelsgesetzbuch)' },
  { value: 'PCG',     label: 'PCG',         description: 'French Plan Comptable Général' },
  { value: 'US_GAAP', label: 'US GAAP',     description: 'Generally Accepted Accounting Principles (US)' },
  { value: 'UK_GAAP', label: 'UK GAAP',     description: 'FRS 102 (United Kingdom)' },
]

const STANDARD_BADGE: Record<string, string> = {
  IAS:     'bg-blue-100 text-blue-800',
  RAS:     'bg-amber-100 text-amber-800',
  HGB:     'bg-slate-100 text-slate-800',
  PCG:     'bg-purple-100 text-purple-800',
  US_GAAP: 'bg-green-100 text-green-800',
  UK_GAAP: 'bg-teal-100 text-teal-800',
}

// ─────────────────── Page ───────────────────
export default function OrganizationSettings() {
  const qc = useQueryClient()
  const { data: org, isLoading } = useQuery({
    queryKey: ['organization'],
    queryFn: async () => (await api.get<Org>('/organization/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (next: Org) =>
      (await api.put<Org>('/organization/', next)).data,
    onSuccess: (updated) => {
      qc.setQueryData(['organization'], updated)
    },
  })

  const [tab, setTab] = useState<'details' | 'subsidiaries'>('details')

  async function save(patch: Partial<Org>) {
    if (!org) return
    await updateMut.mutateAsync({ ...org, ...patch } as Org)
  }
  const onSave = (patch: Partial<Org>) => save(patch).then(() => undefined)

  if (isLoading || !org) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-foreground">
          <InlineText
            value={org.group_name}
            onSave={(v) => onSave({ group_name: v })}
          />
        </h2>
        <div className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap mt-1">
          <span className={`text-xs px-2 py-0.5 rounded font-semibold ${TYPE_COLOR[org.organization_type]}`}>
            {TYPE_LABEL[org.organization_type]}
          </span>
          {org.group_headquarters_location && <span>· {org.group_headquarters_location}</span>}
          {org.group_country && <span>· <span className="font-mono">{org.group_country}</span></span>}
          {org.reporting_currency && <span>· Reports in <span className="font-mono">{org.reporting_currency}</span></span>}
        </div>
      </div>

      {/* Logo upload — small card under the title. */}
      <LogoCard org={org} />

      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'subsidiaries'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'subsidiaries' && 'Subsidiaries'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && <DetailsTab org={org} onSave={onSave} />}
      {tab === 'subsidiaries' && (
        <SubsidiariesTab
          orgType={org.organization_type}
          onSwitchToDetails={() => setTab('details')}
        />
      )}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  org, onSave,
}: {
  org: Org
  onSave: (patch: Partial<Org>) => Promise<void>
}) {
  const isSingleCompany = org.organization_type === 'SINGLE_COMPANY'

  return (
    <div className="space-y-6 max-w-3xl">
      <Section title="Structure">
        <Row label="Organization type">
          <InlineSelect
            value={org.organization_type}
            displayLabel={
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${TYPE_COLOR[org.organization_type]}`}>
                {TYPE_LABEL[org.organization_type]}
              </span>
            }
            options={[
              { value: 'SINGLE_COMPANY', label: 'Single Company' },
              { value: 'HOLDING_SUBSIDIARIES', label: 'Holding with Subsidiaries' },
            ]}
            onSave={(v) => onSave({ organization_type: v as Org['organization_type'] })}
            helper="Switch to Holding to enable the Subsidiaries tab."
          />
        </Row>
      </Section>

      <Section
        title={isSingleCompany ? 'Identity (your legal entity)' : 'Identity (group / holdco)'}
        subtitle={isSingleCompany
          ? 'Your registered legal entity. These fields populate every employment, invoice, and contract issued by your company.'
          : 'Group-level identity. Per-subsidiary legal entities are configured on each Subsidiary record.'}
      >
        <Row label="Group name">
          <InlineText value={org.group_name} onSave={(v) => onSave({ group_name: v })} />
        </Row>
        <Row label="Legal name">
          <InlineText
            value={org.legal_name}
            onSave={(v) => onSave({ legal_name: v })}
            placeholder={isSingleCompany ? 'Registered company name (required)' : 'Holdco legal name (optional)'}
          />
        </Row>
        <Row label="Legal form">
          <InlineText
            value={org.legal_form}
            onSave={(v) => onSave({ legal_form: v })}
            placeholder="SRL, GmbH, LLC, SARL, SA, Ltd…"
          />
        </Row>
        <Row label="Registration #">
          <InlineText
            value={org.registration_number}
            onSave={(v) => onSave({ registration_number: v })}
            placeholder="Commercial register number"
          />
        </Row>
        <Row label="Tax ID">
          <InlineText
            value={org.tax_id}
            onSave={(v) => onSave({ tax_id: v })}
            placeholder="CUI / CIF / EIN — fiscal code (not VAT)"
          />
        </Row>
        <Row label="VAT #">
          <InlineText
            value={org.vat_number}
            onSave={(v) => onSave({ vat_number: v })}
            placeholder="e.g. RO12345678. Blank if not VAT-registered."
          />
        </Row>
        <Row label="Country (ISO 2)">
          <InlineText
            value={org.group_country}
            onSave={(v) => onSave({ group_country: v.toUpperCase().slice(0, 2) })}
            className="font-mono"
            helper="2-letter country code, e.g. RO, DE, US."
          />
        </Row>
        <Row label="Legal address">
          <InlineText
            value={org.legal_address_line1}
            onSave={(v) => onSave({ legal_address_line1: v })}
            placeholder="Street + number"
          />
        </Row>
        <Row label="City">
          <InlineText
            value={org.legal_city}
            onSave={(v) => onSave({ legal_city: v })}
          />
        </Row>
        <Row label="Postal code">
          <InlineText
            value={org.legal_postal_code}
            onSave={(v) => onSave({ legal_postal_code: v })}
          />
        </Row>
        <Row label="Headquarters">
          <InlineText
            value={org.group_headquarters_location}
            onSave={(v) => onSave({ group_headquarters_location: v })}
            placeholder="City, Country (display only)"
          />
        </Row>
        <Row label="Description">
          <InlineText
            value={org.group_description}
            multiline
            onSave={(v) => onSave({ group_description: v })}
            placeholder="Short description of the group"
          />
        </Row>
        <Row label="Logo URL">
          <InlineText
            value={org.group_logo_url}
            onSave={(v) => onSave({ group_logo_url: v })}
            placeholder="https://…"
          />
        </Row>
      </Section>

      <Section title="Accounting">
        <Row label="Accounting standard">
          <InlineSelect
            value={org.accounting_standard || 'IAS'}
            displayLabel={(() => {
              const s = ACCOUNTING_STANDARDS.find(x => x.value === (org.accounting_standard || 'IAS'))
              return (
                <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STANDARD_BADGE[org.accounting_standard || 'IAS'] ?? 'bg-muted text-muted-foreground'}`}>
                  {s?.label ?? org.accounting_standard}
                </span>
              )
            })()}
            options={ACCOUNTING_STANDARDS.map(s => ({ value: s.value, label: `${s.label} — ${s.description}` }))}
            onSave={(v) => onSave({ accounting_standard: v })}
            helper="Framework used for this entity's books. Affects chart of accounts, report layouts, and statutory filings."
          />
        </Row>
      </Section>

      <Section title="Consolidation">
        <Row label="Enable consolidation">
          <InlineStatus
            value={org.consolidation_enabled ? 'ON' : 'OFF'}
            colorMap={ON_OFF_COLOR}
            options={[
              { value: 'ON', label: 'Enabled' },
              { value: 'OFF', label: 'Disabled' },
            ]}
            onSave={(v) => onSave({
              consolidation_enabled: v === 'ON',
              ...(v === 'OFF' ? { consolidation_method: '' as Org['consolidation_method'] } : {}),
            })}
          />
        </Row>
        {org.consolidation_enabled && (
          <>
            <Row label="Consolidation method">
              <InlineSelect
                value={org.consolidation_method || ''}
                displayLabel={org.consolidation_method
                  ? CONSOLIDATION_LABEL[org.consolidation_method]
                  : <Dash />}
                allowClear
                options={[
                  { value: 'FULL', label: 'Full consolidation' },
                  { value: 'EQUITY', label: 'Equity method' },
                  { value: 'PROPORTIONAL', label: 'Proportional' },
                ]}
                onSave={(v) => onSave({ consolidation_method: v as Org['consolidation_method'] })}
              />
            </Row>
            <Row label="Intercompany elimination">
              <InlineStatus
                value={org.intercompany_elimination ? 'ON' : 'OFF'}
                colorMap={ON_OFF_COLOR}
                options={[
                  { value: 'ON', label: 'Eliminated' },
                  { value: 'OFF', label: 'Not eliminated' },
                ]}
                onSave={(v) => onSave({ intercompany_elimination: v === 'ON' })}
              />
            </Row>
            <Row label="Reporting currency">
              <InlineText
                value={org.reporting_currency}
                onSave={(v) => onSave({ reporting_currency: v.toUpperCase().slice(0, 3) })}
                className="font-mono"
                helper="3-letter ISO code, e.g. EUR, USD."
              />
            </Row>
          </>
        )}
      </Section>

      <Section title="Primary contact">
        <Row label="Name">
          <InlineText
            value={org.primary_contact_name}
            onSave={(v) => onSave({ primary_contact_name: v })}
          />
        </Row>
        <Row label="Email">
          <InlineText
            value={org.primary_contact_email}
            onSave={(v) => onSave({ primary_contact_email: v })}
            placeholder="name@company.com"
          />
        </Row>
        <Row label="Phone">
          <InlineText
            value={org.primary_contact_phone}
            onSave={(v) => onSave({ primary_contact_phone: v })}
          />
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, subtitle, children }: {
  title: string
  subtitle?: string
  children: ReactNode
}) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{title}</h3>
      {subtitle && (
        <p className="text-xs text-muted-foreground mb-3 max-w-prose">{subtitle}</p>
      )}
      {!subtitle && <div className="mb-3" />}
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Subsidiaries tab ───────────────────

type SubsidiaryDraft = {
  code: string
  name: string
  legal_form: string
  country_code: string
  ownership_percent: string
  start_date: string | null
}

function emptyDraft(): SubsidiaryDraft {
  return {
    code: '', name: '', legal_form: '',
    country_code: '', ownership_percent: '100',
    start_date: null,
  }
}

function SubsidiariesTab({
  orgType, onSwitchToDetails,
}: {
  orgType: Org['organization_type']
  onSwitchToDetails: () => void
}) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const subsQ = useQuery({
    queryKey: ['subsidiaries'],
    queryFn: async () => (await api.get<Subsidiary[]>('/subsidiaries/')).data,
    enabled: orgType === 'HOLDING_SUBSIDIARIES',
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<SubsidiaryDraft>(emptyDraft())

  function openCreate() { setDraft(emptyDraft()); setCreating(true) }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Subsidiary>('/subsidiaries/', {
      ...draft,
      ownership_percent: draft.ownership_percent === '' ? null : draft.ownership_percent,
      // Defaults for fields not in the create drawer
      registration_number: '',
      tax_id: '',
      address_line1: '',
      city: '',
      postal_code: '',
      end_date: null,
      consolidation_method: '',
      currency_code: '',
      is_active: true,
      notes: '',
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['subsidiaries'] })
      toast.success('Subsidiary created')
      setCreating(false)
      navigate(`/admin/organization/subsidiaries/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/subsidiaries/${id}/`),
    onSuccess: () => {
      toast.success('Subsidiary deleted')
      qc.invalidateQueries({ queryKey: ['subsidiaries'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  // Empty state when org_type is SINGLE_COMPANY
  if (orgType === 'SINGLE_COMPANY') {
    return (
      <div className="bg-card rounded-lg shadow p-12 text-center max-w-2xl">
        <p className="text-foreground font-medium mb-2">Subsidiaries are only available for holding structures.</p>
        <p className="text-sm text-muted-foreground mb-6">
          Your organization is set up as a <strong>Single Company</strong>. To track owned subsidiaries,
          switch the organization type to <strong>Holding with Subsidiaries</strong> on the Details tab.
        </p>
        <Button variant="outline" onClick={onSwitchToDetails}>
          Open Details tab
        </Button>
      </div>
    )
  }

  const subs = subsQ.data || []

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Legal entities owned by the holding. Click a row to open the subsidiary record page.
        </p>
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add subsidiary</Button>
      </div>

      {subsQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : subs.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No subsidiaries yet. Click "+ Add subsidiary" above to create one.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Legal form</Th>
                <Th>Country</Th>
                <Th className="text-right">Owned</Th>
                <Th>Period</Th>
                <Th>Active</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {subs.map((s) => (
                <tr
                  key={s.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/organization/subsidiaries/${s.id}`)}
                >
                  <Td className="font-mono text-xs">{s.code}</Td>
                  <Td className="font-medium">{s.name}</Td>
                  <Td className="text-xs">{s.legal_form || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="font-mono text-xs">{s.country_code || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-right font-mono text-xs">
                    {s.ownership_percent
                      ? `${Number(s.ownership_percent).toFixed(2)}%`
                      : <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td className="text-xs">
                    {s.start_date || '—'} → {s.end_date || <span className="text-muted-foreground">open</span>}
                  </Td>
                  <Td className="text-xs">
                    <span className={`inline-block w-2 h-2 rounded-full ${s.is_active ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/organization/subsidiaries/${s.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => confirm(
                            `subsidiary ${s.code} (${s.name})`,
                            () => deleteMut.mutate(s.id),
                          )}
                          className="text-destructive"
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New subsidiary"
        description="Create the subsidiary with the essentials. Address, tax IDs, consolidation overrides, and notes are filled in on the subsidiary's record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="sub-code">Code *</FieldLabel>
              <Input id="sub-code" autoFocus required value={draft.code}
                onChange={(ev) => setDraft({ ...draft, code: ev.target.value })}
                className="font-mono" placeholder="e.g. DE-SUB-01" />
            </Field>
            <Field>
              <FieldLabel htmlFor="sub-legal">Legal form</FieldLabel>
              <Input id="sub-legal" value={draft.legal_form}
                onChange={(ev) => setDraft({ ...draft, legal_form: ev.target.value })}
                placeholder="GmbH, LLC, SARL, SA, Ltd, …" />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="sub-name">Name *</FieldLabel>
            <Input id="sub-name" required value={draft.name}
              onChange={(ev) => setDraft({ ...draft, name: ev.target.value })}
              placeholder="Legal name" />
          </Field>

          <div className="grid grid-cols-3 gap-4">
            <Field>
              <FieldLabel htmlFor="sub-country">Country (ISO 2)</FieldLabel>
              <Input id="sub-country" maxLength={2} value={draft.country_code}
                onChange={(ev) => setDraft({ ...draft, country_code: ev.target.value.toUpperCase() })}
                className="font-mono" />
            </Field>
            <Field>
              <FieldLabel htmlFor="sub-pct">Ownership %</FieldLabel>
              <Input id="sub-pct" type="number" step="0.01" min="0" max="100"
                value={draft.ownership_percent}
                onChange={(ev) => setDraft({ ...draft, ownership_percent: ev.target.value })}
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="sub-start">Start date</FieldLabel>
              <DatePicker id="sub-start" value={draft.start_date || ''}
                onChange={(v) => setDraft({ ...draft, start_date: v || null })} />
              <FieldDescription>When acquired or formed.</FieldDescription>
            </Field>
          </div>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}

// ─────────────────── Logo upload card ───────────────────

function LogoCard({ org }: { org: Org }) {
  const qc = useQueryClient()
  const inputId = 'org-logo-file'

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('logo', file)
      return (await api.post('/organization/logo/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['organization'] })
      qc.invalidateQueries({ queryKey: ['org', 'singleton'] })
      toast.success('Logo updated')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Upload failed'),
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete('/organization/logo/'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['organization'] })
      qc.invalidateQueries({ queryKey: ['org', 'singleton'] })
      toast.success('Logo removed')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Delete failed'),
  })

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Logo too large; 5MB max.')
      return
    }
    uploadMut.mutate(f)
    e.target.value = ''  // allow re-uploading the same file
  }

  // Cache-buster so the preview updates immediately after upload.
  const previewSrc = org.logo_url
    ? `${org.logo_url}${org.updated_at ? '?v=' + encodeURIComponent(org.updated_at) : ''}`
    : ''

  return (
    <div className="mb-6 bg-card rounded-lg shadow p-4 max-w-3xl">
      <div className="flex items-center gap-4">
        {previewSrc ? (
          <img
            src={previewSrc}
            alt="Organization logo"
            className="w-20 h-20 rounded-md object-cover bg-muted border border-border"
          />
        ) : (
          <div className="w-20 h-20 rounded-md bg-muted border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground text-center px-2">
            No logo
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Organization logo</div>
          <div className="text-xs text-muted-foreground">
            Shown in the sidebar header + future document letterheads.
            PNG / JPG / SVG up to 5MB. Square images look best.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            id={inputId} type="file" accept="image/*"
            className="hidden" onChange={onPick}
          />
          <label
            htmlFor={inputId}
            className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
          >
            {previewSrc ? 'Replace' : 'Upload'}
          </label>
          {previewSrc && (
            <button
              type="button"
              onClick={() => deleteMut.mutate()}
              disabled={deleteMut.isPending}
              className="text-xs text-destructive hover:underline"
            >
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
